import { useEffect, useState } from "react";

import { getStorageSafely } from "../storage/getStorage";
import logger from "../logger/logger";

export const useStatePersisted = <T,>(
  id: any,
  name: string,
  defaultValue: T,
  useSessionStorage?: boolean,
  forceTypeWhenDefaultValueIsNull?: "string" | "number" | "boolean" | "object",
) => {
  const localStorageKey = (id) => `useStatePersisted-${name}${id ? "-" + id : ""}`;
  const storage = getStorageSafely(useSessionStorage ? "session" : "local");
  if (!storage) {
    return useState<T>(defaultValue);
  }

  const storageValueString = storage?.getItem(localStorageKey(id));
  const parsedValue = parseValueFromString(storageValueString, forceTypeWhenDefaultValueIsNull ?? typeof defaultValue);
  const [stateValue, stateSet] = useState<T>(parsedValue !== undefined ? (parsedValue as T) : defaultValue);
  useEffect(() => storeState(localStorageKey(id), stateValue), [stateValue]);
  const clearPersistence = () => {
    storage?.removeItem(localStorageKey(id));
  };
  return [stateValue, stateSet, clearPersistence] as const;
  function storeState(key: string, value: T) {
    let stringValue = value as string;
    if (forceTypeWhenDefaultValueIsNull === "object" || typeof value === "object") {
      stringValue = JSON.stringify(value);
    }

    try {
      storage?.setItem(key, stringValue);
    } catch (error) {
      logger.windowOnErrorHandler(
        `Failed to save ${key} in ${useSessionStorage ? "sessionStorage" : "localStorage"} in useStatePersisted`,
        location.href,
        "",
        "",
        error,
      );
    }
  }
};

function parseValueFromString(stringValue: string | null, type: string) {
  if (!stringValue || stringValue === "undefined") {
    return undefined;
  }

  switch (type) {
    case "string":
      return stringValue;
    case "number":
      return parseNumber(stringValue);
    case "boolean":
      return stringValue == "true";
    case "object":
      return stringValue ? JSON.parse(stringValue) : undefined;
  }
  throw Error(`Type: ${type} not supported in useStatePersisted`);
}

function parseNumber(stringNumber) {
  const value = parseInt(stringNumber || "");
  if (isNaN(value)) {
    return undefined;
  }
  return value;
}

export default useStatePersisted;
